import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { calendarAPI } from "../lib/api";
import { formatDate } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { ChevronLeft, ChevronRight, FileText, CheckSquare, FolderKanban, Receipt, Briefcase } from "lucide-react";

const EVENT_COLORS = {
  task: "bg-blue-500",
  project: "bg-purple-500",
  invoice: "bg-yellow-500",
  quote: "bg-green-500",
  lead: "bg-pink-500",
};

const EVENT_ICONS = {
  task: CheckSquare,
  project: FolderKanban,
  invoice: Receipt,
  quote: FileText,
  lead: Briefcase,
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    fetchEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  const fetchEvents = async () => {
    try {
      // Get events for current month +/- 1 month
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 2, 0);
      
      const res = await calendarAPI.getEvents({
        start_date: startDate.toISOString().split("T")[0],
        end_date: endDate.toISOString().split("T")[0],
      });
      setEvents(res.data);
    } catch (err) {
      console.error("Error fetching events:", err);
    } finally {
      setLoading(false);
    }
  };

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split("T")[0];
    return events.filter((e) => e.date === dateStr);
  };

  const selectedDateEvents = getEventsForDate(selectedDate);

  // Get dates that have events for calendar highlighting
  const eventDates = events.reduce((acc, event) => {
    acc[event.date] = true;
    return acc;
  }, {});

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleEventClick = (event) => {
    if (event.type === "lead") {
      navigate("/leads");
    } else if (event.type === "invoice") {
      navigate(`/invoices/${event.id}`);
    } else if (event.type === "quote") {
      navigate(`/quotes/${event.id}`);
    } else {
      // Tasks and projects go to client workspace
      const clientEvent = events.find(e => e.id === event.id);
      if (clientEvent?.link) {
        navigate(clientEvent.link);
      }
    }
  };

  // Custom day render for calendar
  const modifiers = {
    hasEvent: (date) => {
      const dateStr = date.toISOString().split("T")[0];
      return eventDates[dateStr];
    },
  };

  const modifiersStyles = {
    hasEvent: {
      fontWeight: "bold",
    },
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Calendar</h1>
        <p className="text-muted-foreground mt-1">
          View all your deadlines, due dates, and follow-ups
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>
                {currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" onClick={handlePrevMonth}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={handleNextMonth}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                month={currentMonth}
                onMonthChange={setCurrentMonth}
                modifiers={modifiers}
                modifiersStyles={modifiersStyles}
                className="rounded-md border w-full"
                classNames={{
                  months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                  month: "space-y-4 w-full",
                  caption: "hidden",
                  table: "w-full border-collapse",
                  head_row: "flex w-full",
                  head_cell: "text-muted-foreground rounded-md w-full font-normal text-[0.8rem]",
                  row: "flex w-full mt-2",
                  cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent w-full",
                  day: "h-12 w-full p-0 font-normal aria-selected:opacity-100 hover:bg-accent rounded-md",
                  day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                  day_today: "bg-accent text-accent-foreground",
                  day_outside: "text-muted-foreground opacity-50",
                }}
                components={{
                  DayContent: ({ date }) => {
                    const dayEvents = getEventsForDate(date);
                    return (
                      <div className="w-full h-full flex flex-col items-center justify-center">
                        <span>{date.getDate()}</span>
                        {dayEvents.length > 0 && (
                          <div className="flex gap-0.5 mt-0.5">
                            {dayEvents.slice(0, 3).map((event, i) => (
                              <div
                                key={i}
                                className={`w-1.5 h-1.5 rounded-full ${EVENT_COLORS[event.type]}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  },
                }}
              />
            </CardContent>
          </Card>
        </div>

        {/* Selected Date Events */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {selectedDate.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin h-6 w-6 border-4 border-primary border-t-transparent rounded-full" />
                </div>
              ) : selectedDateEvents.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No events for this date
                </p>
              ) : (
                <div className="space-y-3">
                  {selectedDateEvents.map((event) => {
                    const Icon = EVENT_ICONS[event.type];
                    return (
                      <div
                        key={`${event.type}-${event.id}`}
                        className={`p-3 rounded-lg border cursor-pointer hover:bg-muted transition-colors ${
                          event.completed ? "opacity-60" : ""
                        }`}
                        onClick={() => handleEventClick(event)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${EVENT_COLORS[event.type]} text-white`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium truncate ${event.completed ? "line-through" : ""}`}>
                              {event.title}
                            </p>
                            {event.client_name && (
                              <p className="text-sm text-muted-foreground">{event.client_name}</p>
                            )}
                            <Badge variant="outline" className="mt-1 capitalize text-xs">
                              {event.type}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Legend */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-sm">Legend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(EVENT_COLORS).map(([type, color]) => {
                  const Icon = EVENT_ICONS[type];
                  return (
                    <div key={type} className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${color}`} />
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm capitalize">{type}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Upcoming Events List */}
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Events</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-6 w-6 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="space-y-2">
              {events
                .filter((e) => new Date(e.date) >= new Date() && !e.completed)
                .slice(0, 10)
                .map((event) => {
                  const Icon = EVENT_ICONS[event.type];
                  const isToday = event.date === new Date().toISOString().split("T")[0];
                  const isPast = new Date(event.date) < new Date();
                  
                  return (
                    <div
                      key={`${event.type}-${event.id}`}
                      className={`flex items-center gap-4 p-3 rounded-lg border cursor-pointer hover:bg-muted transition-colors ${
                        isPast ? "border-red-300 bg-red-50 dark:bg-red-900/10" : ""
                      } ${isToday ? "border-blue-300 bg-blue-50 dark:bg-blue-900/10" : ""}`}
                      onClick={() => handleEventClick(event)}
                    >
                      <div className={`p-2 rounded-lg ${EVENT_COLORS[event.type]} text-white`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{event.title}</p>
                        {event.client_name && (
                          <p className="text-sm text-muted-foreground">{event.client_name}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-medium ${isPast ? "text-red-600" : isToday ? "text-blue-600" : ""}`}>
                          {isToday ? "Today" : formatDate(event.date)}
                        </p>
                        <Badge variant="outline" className="capitalize text-xs">
                          {event.type}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              {events.filter((e) => new Date(e.date) >= new Date() && !e.completed).length === 0 && (
                <p className="text-muted-foreground text-center py-8">
                  No upcoming events
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
